<template>
<div class="mt-4">
  {{ name }} &nbsp;
  <v-tooltip bottom v-if="info">
    <template v-slot:activator="{ on, attrs }">
      <v-icon color="primary" dark v-bind="attrs" v-on="on" small>mdi-information-outline</v-icon>
    </template>
    <span>{{ info }}</span>
  </v-tooltip>
  <vue-apex-charts :id="name" width="100%" height="200" type="line" :options="chartOptions" :series="chartSeries"></vue-apex-charts>
  <template v-for="(change, index) in externalSeriesChanges">
    <external-series-chip v-if="change !== null" :key="`change_${index}`" v-model="externalSeriesChanges[index]" :label="formatDate(dates[start + index])" :min="series.min" :max="series.max" @kill="deleteExternalSeriesChange(index)" />
  </template>
</div>
</template>

<script>
import ExternalSeriesChip from '@/components/ExternalSeriesChip.vue'
import VueApexCharts from 'vue-apexcharts'
import numbro from 'numbro'
import moment from 'moment'

export default {
  data: function () {
    return {
      externalSeriesChanges: new Array(this.length).fill(null)
    }
  },
  components: {
    VueApexCharts,
    ExternalSeriesChip
  },
  computed: {
    mantissa: function () {
      const values = this.series.default_values.map(value => {
        const val = value.toString()
        const pointIndex = val.indexOf('.') + 1
        return pointIndex ? val.length - pointIndex : 0
      })
      return Math.max(...values)
    },
    chartOptions: function () {
      return {
        chart: {
          id: this.name,
          toolbar: {
            show: false
          },
          events: {
            markerClick: this.markerClick
          },
          zoom: {
            enabled: false
          }
        },
        yaxis: {
          labels: {
            formatter: this.formatDataLabel
          }
        },
        xaxis: {
          type: 'categories',
          categories: this.dates.slice(this.start, this.start + this.length),
          labels: {
            show: true,
            hideOverlappingLabels: true,
            formatter: this.formatDate
          },
          axisTicks: {
            show: false
          }
        },
        colors: ['rgb(186, 151, 101)', 'rgb(178,34,34)', 'rgb(178,34,34)'],
        stroke: {
          width: [3, 1, 1],
          curve: 'straight',
          dashArray: [0, 3, 3]
        },
        legend: {
          show: false
        },
        tooltip: {
          enabledOnSeries: [0, 1, 2]
        },
        annotations: {
          points: this.selectedPoints
          /* yaxis: [
            {
              y: this.series.max,
              label: {
                borderColor: 'rgb(178,34,34)',
                style: {
                  color: '#fff',
                  background: 'rgb(178,34,34)'
                },
                text: `Max: ${this.formatDataLabel(this.series.max)}`
              }
            },
            {
              y: this.series.min,
              label: {
                borderColor: 'rgb(178,34,34)',
                style: {
                  color: '#fff',
                  background: 'rgb(178,34,34)'
                },
                text: `Min: ${this.formatDataLabel(this.series.min)}`
              }
            }
          ] */
        }
      }
    },
    chartSeries: function () {
      var data = this.series.default_values.slice(this.start, this.start + this.length)
      var lastChange = null
      for (var i = 0; i < this.length; i++) {
        if (this.externalSeriesChanges[i] !== null) {
          lastChange = this.externalSeriesChanges[i]
        }
        if (this.propagateExternalSeriesChanges && lastChange) {
          data[i] = lastChange
        } else if (!this.propagateExternalSeriesChanges && this.externalSeriesChanges[i]) {
          data[i] = this.externalSeriesChanges[i]
        }
      }
      let returnArray = [{
        name: this.name,
        data: data
      }]
      if (!isNaN(this.series.min)) {
        returnArray.push({
          name: this.$t('components.ExternalSeriesChart.min'),
          data: new Array(data.length).fill(this.series.min)
        })
      }
      if (!isNaN(this.series.max)) {
        returnArray.push({
          name: this.$t('components.ExternalSeriesChart.max'),
          data: new Array(data.length).fill(this.series.max)
        })
      }
      return returnArray
    },
    selectedPoints: function () {
      var points = this.externalSeriesChanges.map((item, index) => {
        if (item !== null) {
          return {
            x: this.formatDate(this.dates[this.start + index]),
            y: item,
            marker: {
              size: 4
            }
          }
        }
      })
      return points
    }
  },
  methods: {
    emitChanges: function () {
      this.$emit('input', this.externalSeriesChanges)
    },
    validateExternalSeriesChange: function (seriesKey, dataPointIndex, value) {
      var min = this.series.min
      var max = this.series.max

      if (!value) {
        this.deleteExternalSeriesChange(seriesKey, dataPointIndex)
      } else if (value > max) {
        this.addExternalSeriesChange(seriesKey, dataPointIndex, max)
      } else if (value < min) {
        this.addExternalSeriesChange(seriesKey, dataPointIndex, min)
      } else {
        this.addExternalSeriesChange(seriesKey, dataPointIndex, value)
      }
    },
    markerClick: function (event, t, data) {
      this.addExternalSeriesChange(data.dataPointIndex, data.w.globals.series[0][data.dataPointIndex])
    },
    deleteExternalSeriesChange: function (dataPointIndex) {
      this.$set(this.externalSeriesChanges, dataPointIndex, null)
      this.emitChanges()
    },
    addExternalSeriesChange: function (dataPointIndex, value) {
      if (value !== null) {
        console.log(`Set value: ${value}, for dataPointIndex: ${dataPointIndex}`)
        this.$set(this.externalSeriesChanges, Number(dataPointIndex), Number(value))
      }
      this.emitChanges()
    },
    formatDataLabel: function (value) {
      return numbro(value).format({
        thousandSeparated: true,
        mantissa: this.mantissa,
        optionalMantissa: false,
        trimMantissa: true
      })
    },
    formatGeneral: function (value, format) {
      return numbro(value).format(format)
    },
    formatDate (date) {
      if (this.modelFrequency === 'month') {
        return moment(date).format('MMM')
      } else {
        return moment(date).format('YYYY-MM-DD')
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler: function () {
        if (this.value && this.value.length > 0 && this.value !== this.externalSeriesChanges) {
          this.externalSeriesChanges = this.value
        }
      }
    }
  },
  props: {
    value: {
      type: Array
    },
    series: {
      type: Object,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    dates: {
      type: Array,
      required: true
    },
    start: {
      type: Number,
      required: false,
      default: function () {
        return 0
      }
    },
    length: {
      type: Number,
      required: false,
      default: function () {
        return this.dates.length
      }
    },
    propagateExternalSeriesChanges: {
      type: Boolean,
      required: false,
      default: function () {
        return true
      }
    },
    info: {
      type: String,
      required: false
    },
    modelFrequency: {
      type: String,
      required: false,
      default: function () {
        return 'month'
      }
    }
  }
}
</script>
